import { useState } from 'react';
import PropTypes from 'prop-types';
import { HelperText, Input, InputRow, Select } from '@/ui';
import ComunaCombo from './ComunaCombo';
import {
  ACTUATION_OPTIONS,
  ADDRESS_TYPE_OPTIONS,
  RECIPIENTS_MAX,
  REPRESENTATIVES_MAX,
  emptyDomicile,
  newRecipient,
  orderRecipients,
  recipientTitle,
} from './actuacionForm';

// "Qué se pide" in the diligencia form (REC-1966, option C): the actuation,
// its inscription or description when it needs one, and the recipients as
// counters, with a domicile of their own as the exception. The state and its
// rules live in actuacionForm.js; the form owns the state.

// The shared Button takes no aria props, and these buttons are named by them
// (the counter's −/+ and the pressed kind of address), so they are native
// buttons with its classes.
function SmallButton({ pressed, className = '', ...props }) {
  const variant = pressed ? 'btn-primary' : 'btn-secondary';
  return (
    <button
      type="button"
      className={`btn btn-sm ${variant}${className ? ` ${className}` : ''}`}
      aria-pressed={pressed}
      {...props}
    />
  );
}

SmallButton.propTypes = {
  pressed: PropTypes.bool,
  className: PropTypes.string,
};

function Counter({ label, value, min, max, onChange, lessLabel, moreLabel }) {
  return (
    <div className="ndf-counter" role="group" aria-label={label}>
      <SmallButton
        aria-label={lessLabel}
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
      >
        −
      </SmallButton>
      <output className="ndf-counter__n" aria-live="polite">
        {value}
      </output>
      <SmallButton
        aria-label={moreLabel}
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
      >
        +
      </SmallButton>
    </div>
  );
}

Counter.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  lessLabel: PropTypes.string.isRequired,
  moreLabel: PropTypes.string.isRequired,
};

// The kind of place an address is, optional: pressing the chosen one again
// leaves it unsaid.
export function AddressTypePicker({ label, value, onChange }) {
  return (
    <div className="ndf-seg" role="group" aria-label={label}>
      {ADDRESS_TYPE_OPTIONS.map((o) => (
        <SmallButton
          key={o.value}
          pressed={value === o.value}
          onClick={() => onChange(value === o.value ? '' : o.value)}
        >
          {o.label}
        </SmallButton>
      ))}
    </div>
  );
}

AddressTypePicker.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function ActuacionFields({
  value,
  onChange,
  errors,
  showErrors,
  recentComunas = [],
}) {
  const { recipients } = value;
  const personas = recipients.filter((r) => r.kind !== 'EMPRESA');
  const empresas = recipients.filter((r) => r.kind === 'EMPRESA');
  const full = recipients.length >= RECIPIENTS_MAX;
  const [domiciliosOpen, setDomiciliosOpen] = useState(() =>
    recipients.some((r) => r.domicile),
  );

  const set = (patch) => onChange({ ...value, ...patch });
  const setRecipients = (list) => set({ recipients: orderRecipients(list) });
  const updateRecipient = (key, patch) =>
    setRecipients(
      recipients.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  const updateDomicile = (r, patch) =>
    updateRecipient(r.key, { domicile: { ...r.domicile, ...patch } });
  const setConservador = (patch) =>
    set({ conservador: { ...value.conservador, ...patch } });

  function setPersonas(n) {
    if (n > personas.length) {
      setRecipients([...recipients, newRecipient('PERSONA')]);
    } else if (n < personas.length) {
      const last = personas[personas.length - 1];
      setRecipients(recipients.filter((r) => r !== last));
    }
  }

  const show = (k) => Boolean(showErrors && errors[k]);

  return (
    <>
      <InputRow label="Actuación" error={show('actuationType')}>
        <Select
          placeholder="Elige la actuación"
          value={value.actuationType}
          error={show('actuationType')}
          onChange={(e) => set({ actuationType: e.target.value })}
        >
          {ACTUATION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        {show('actuationType') && (
          <HelperText kind="err">{errors.actuationType}</HelperText>
        )}
      </InputRow>

      {value.actuationType === 'OTRA' && (
        <InputRow label="¿Qué actuación?" error={show('actuationTypeOther')}>
          <Input
            placeholder="Ej.: retiro de especies"
            maxLength={200}
            value={value.actuationTypeOther}
            error={show('actuationTypeOther')}
            onChange={(e) => set({ actuationTypeOther: e.target.value })}
          />
          {show('actuationTypeOther') && (
            <HelperText kind="err">{errors.actuationTypeOther}</HelperText>
          )}
        </InputRow>
      )}

      {value.actuationType === 'GESTION_CONSERVADOR' && (
        <fieldset className="ndf-group">
          <legend className="ndf-group__legend">Inscripción</legend>
          <div className="ndf-grid4">
            {[
              ['fojas', 'Fojas', '1234 vta'],
              ['numero', 'Número', '1567'],
              ['anio', 'Año', '2019'],
              ['registro', 'Registro', 'Propiedad'],
            ].map(([k, label, placeholder]) => (
              <InputRow key={k} label={label} error={show(k)}>
                <Input
                  variant="mono"
                  placeholder={placeholder}
                  value={value.conservador[k]}
                  error={show(k)}
                  onChange={(e) => setConservador({ [k]: e.target.value })}
                />
                {show(k) && <HelperText kind="err">{errors[k]}</HelperText>}
              </InputRow>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="ndf-group">
        <legend className="ndf-group__legend">Destinatarios</legend>
        <div className="ndf-recipient-row">
          <span>Personas naturales</span>
          <Counter
            label="Personas naturales"
            value={personas.length}
            min={empresas.length > 0 ? 0 : 1}
            max={full ? personas.length : RECIPIENTS_MAX}
            onChange={setPersonas}
            lessLabel="Una persona natural menos"
            moreLabel="Una persona natural más"
          />
        </div>
        {empresas.map((r, i) => (
          <div className="ndf-recipient-row" key={r.key}>
            <span>Empresa {i + 1} · representantes</span>
            <Counter
              label={`Representantes de la empresa ${i + 1}`}
              value={r.representatives}
              min={1}
              max={REPRESENTATIVES_MAX}
              onChange={(n) => updateRecipient(r.key, { representatives: n })}
              lessLabel={`Un representante menos en la empresa ${i + 1}`}
              moreLabel={`Un representante más en la empresa ${i + 1}`}
            />
            <button
              type="button"
              className="ndf-link"
              aria-label={`Quitar la empresa ${i + 1}`}
              disabled={recipients.length <= 1}
              onClick={() =>
                setRecipients(recipients.filter((x) => x.key !== r.key))
              }
            >
              Quitar
            </button>
          </div>
        ))}
        <button
          type="button"
          className="ndf-link"
          disabled={full}
          onClick={() =>
            setRecipients([...recipients, newRecipient('EMPRESA')])
          }
        >
          + Agregar empresa
        </button>
        {full && (
          <HelperText>
            Una solicitud lleva hasta {RECIPIENTS_MAX} destinatarios.
          </HelperText>
        )}
        {show('recipients') && (
          <HelperText kind="err">{errors.recipients}</HelperText>
        )}

        {domiciliosOpen ? (
          <div className="ndf-domicilios">
            {recipients.map((r, i) => (
              <fieldset className="ndf-domicilio" key={r.key}>
                <legend className="ndf-domicilio__who">
                  {recipientTitle(r, i)}
                </legend>
                {r.domicile ? (
                  <>
                    <InputRow
                      label="Dirección"
                      error={show(`${r.key}.address`)}
                    >
                      <Input
                        placeholder="Av. Providencia 1208"
                        value={r.domicile.address}
                        error={show(`${r.key}.address`)}
                        onChange={(e) =>
                          updateDomicile(r, { address: e.target.value })
                        }
                      />
                      {show(`${r.key}.address`) && (
                        <HelperText kind="err">
                          {errors[`${r.key}.address`]}
                        </HelperText>
                      )}
                    </InputRow>
                    <InputRow label="Detalle">
                      <Input
                        placeholder="Oficina / depto / piso"
                        value={r.domicile.addressDetails}
                        onChange={(e) =>
                          updateDomicile(r, { addressDetails: e.target.value })
                        }
                      />
                    </InputRow>
                    <InputRow
                      label="Comuna · Región"
                      error={show(`${r.key}.comuna`)}
                    >
                      <ComunaCombo
                        value={r.domicile.comunaText}
                        recent={recentComunas}
                        error={show(`${r.key}.comuna`)}
                        onChange={(comunaText) =>
                          updateDomicile(r, { comunaText })
                        }
                      />
                      {show(`${r.key}.comuna`) && (
                        <HelperText kind="err">
                          {errors[`${r.key}.comuna`]}
                        </HelperText>
                      )}
                    </InputRow>
                    <div className="ndf-recipient-row">
                      <span>Tipo de domicilio</span>
                      <AddressTypePicker
                        label={`Tipo de domicilio del destinatario ${i + 1}`}
                        value={r.domicile.addressType}
                        onChange={(addressType) =>
                          updateDomicile(r, { addressType })
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="ndf-link"
                      onClick={() => updateRecipient(r.key, { domicile: null })}
                    >
                      Usar el domicilio de la diligencia
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="ndf-link"
                    onClick={() =>
                      updateRecipient(r.key, { domicile: emptyDomicile() })
                    }
                  >
                    Tiene otro domicilio
                  </button>
                )}
              </fieldset>
            ))}
          </div>
        ) : (
          <HelperText>
            Todos se notifican en la dirección de la diligencia.{' '}
            <button
              type="button"
              className="ndf-link"
              onClick={() => setDomiciliosOpen(true)}
            >
              Alguno tiene otro domicilio
            </button>
          </HelperText>
        )}
      </fieldset>
    </>
  );
}

ActuacionFields.propTypes = {
  value: PropTypes.shape({
    actuationType: PropTypes.string.isRequired,
    actuationTypeOther: PropTypes.string.isRequired,
    conservador: PropTypes.object.isRequired,
    addressType: PropTypes.string.isRequired,
    recipients: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  showErrors: PropTypes.bool,
  recentComunas: PropTypes.array,
};
